import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { localDayKey, startOfLocalDay } from '@wf/shared'

/** Sekunden-Tick für Live-Uhr/-Zähler. `active=false` stoppt den Timer (kein Render-Loop im Ruhezustand). */
export function useNow(active = true, intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    const handle = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(handle)
  }, [active, intervalMs])
  return now
}

/**
 * Heute gearbeitete Sekunden inkl. laufendem Segment — aus dem server-persistierten `liveSince`
 * (NIE aus einer RAM-Uhr, CLAUDE.md-Regel; identische Rechnung wie Header-Uhr/AttendanceBar).
 * Der Live-Anteil wird client-seitig an der Werkstatt-Mitternacht GEKLEMMT: Der Server klemmt nur zum
 * Fetch-Zeitpunkt — auf einem dauer-offenen Tablet liefe ein Vortags-liveSince sonst ungebremst in den
 * neuen Tag hinein (Review-Fund Mitternachts-Rollover; die Rollover-Invalidierung holt danach frisch).
 */
export function liveWorkedSeconds(state: { todaySeconds: number; liveSince: number | null }, nowMs: number): number {
  if (state.liveSince == null) return state.todaySeconds
  const from = Math.max(state.liveSince, startOfLocalDay(nowMs))
  return state.todaySeconds + Math.max(0, Math.floor((nowMs - from) / 1000))
}

/**
 * Tageswechsel-Wächter: Sobald der Werkstatt-Kalendertag umspringt (Tablet bleibt über Mitternacht
 * offen, kein Fokus-Event), sind Anwesenheits-Snapshot und alle Zeiten-Queries Vortags-Stand →
 * gezielt invalidieren, damit todaySeconds/liveSince/Report frisch vom Server kommen (B-5-Split).
 */
export function useDayRolloverInvalidation(): string {
  const qc = useQueryClient()
  const now = useNow(true, 30_000) // Minuten-Genauigkeit reicht fürs Umspringen; kein Sekunden-Ticker nötig
  const today = localDayKey(now)
  const prev = useRef(today)
  useEffect(() => {
    if (prev.current === today) return
    prev.current = today
    void qc.invalidateQueries({ queryKey: ['attendance'] })
    void qc.invalidateQueries({ queryKey: ['zeiten'] })
  }, [today, qc])
  return today
}
